import React, { useState, useEffect, useCallback, useMemo } from "react";
import API_BASE_URL from "../config";
import Swal from "sweetalert2";
import ViewSOModal from "./ViewSOModal";
import {
    Eye, RefreshCw, X, Search, Plus, Trash2, Save,
    FileText, Download, Truck, Users, Building2, CalendarDays,
    CheckSquare, Square
} from "lucide-react";
import { createPortal } from "react-dom";

// ── Constants ────────────────────────────────────────────────────────────────
const PAGE_SIZE = 10;
const DRAFT_KEY = "scrap_loading_draft";
const TOTAL_ROWS = 1;

const SCRAP_SUGGESTIONS = [
    "GOOD KARTON", "NON PAD", "SCRAP", "PLASTIK SCRAP",
    "FLUFF PULP", "DIAPER SCRAP", "NAPKIN SCRAP", "TUBE SCRAP",
    "MIXED PAPER", "CORRUGATE",
];
const UOM_OPTIONS = ["KGS", "PCS", "ROLLS", "BALES", "BAGS"];

const gradHdr = {
    background: "linear-gradient(135deg, #0c1445 0%, #1a2f7a 50%, #1e40af 100%)",
};

const esc = (s) =>
    String(s ?? "").replace(/[&<>"']/g, (c) =>
        ({ "&": "&amp;", "<": "&lt;", ">": "&gt;", '"': "&quot;", "'": "&#39;" }[c])
    );

// ── Empty helpers ─────────────────────────────────────────────────────────────
const EMPTY_ITEM = () => ({ scrapCodeName: "", tagNo: "", quantity: "", uom: "" });
const EMPTY_ITEMS = (n = TOTAL_ROWS) => Array.from({ length: n }, EMPTY_ITEM); const EMPTY_FORM = () => ({
    controlNo: "",
    formDate: new Date().toISOString().split("T")[0],
    customerName: "",
    scrapFromCaloocan: true,
    scrapFromCDO: false,
    remarks: "",
    preparedBy: "",
    checkedBy: "",
    receivedBy: "",
    items: EMPTY_ITEMS(),
});

// ── Field accessors (handles PascalCase from API) ─────────────────────────────
const f = {
    id: r => r.id || r.Id || 0,
    controlNo: r => r.controlNo || r.ControlNo || "",
    formDate: r => r.formDate || r.FormDate || "",
    customerName: r => r.customerName || r.CustomerName || "",
    caloocan: r => r.scrapFromCaloocan ?? r.ScrapFromCaloocan ?? false,
    cdo: r => r.scrapFromCDO ?? r.ScrapFromCDO ?? false,
    remarks: r => r.remarks || r.Remarks || "",
    preparedBy: r => r.preparedBy || r.PreparedBy || "",
    checkedBy: r => r.checkedBy || r.CheckedBy || "",
    receivedBy: r => r.receivedBy || r.ReceivedBy || "",
    items: r => r.items || r.Items || [],
};

// ── PDF Export ────────────────────────────────────────────────────────────────
const exportPDF = async (data) => {
    if (!window.html2pdf) {
        await new Promise((resolve, reject) => {
            const s = document.createElement("script");
            s.src = "https://cdnjs.cloudflare.com/ajax/libs/html2pdf.js/0.10.1/html2pdf.bundle.min.js";
            s.onload = resolve; s.onerror = reject;
            document.head.appendChild(s);
        });
    }

    const controlNo = f.controlNo(data);
    const items = f.items(data);
    const caloocan = f.caloocan(data);
    const cdo = f.cdo(data);



    const rowsHTML = Array.from({ length: TOTAL_ROWS }, (_, i) => {
        const item = items[i] || {};
        const bg = i % 2 === 0 ? "#fff" : "#f8fafc";
        return `<tr style="background:${bg}">
            <td style="padding:3px 6px;text-align:center;border:0.5px solid #e2e8f0;font-size:8px;color:#94a3b8">${i + 1}</td>
            <td style="padding:3px 8px;border:0.5px solid #e2e8f0;font-size:8.5px;font-weight:${item.scrapCodeName ? "700" : "400"};color:#0f172a">${esc(item.scrapCodeName || "")}</td>
            <td style="padding:3px 6px;text-align:center;border:0.5px solid #e2e8f0;font-size:8.5px;color:#334155">${esc(item.tagNo || "")}</td>
            <td style="padding:3px 8px;text-align:right;border:0.5px solid #e2e8f0;font-size:8.5px;font-weight:600;color:#0f172a">${item.quantity ?? ""}</td>
            <td style="padding:3px 6px;text-align:center;border:0.5px solid #e2e8f0;font-size:8px;color:#64748b">${esc(item.uom || "")}</td>
        </tr>`;
    }).join("");

    const html = `
    <div style="font-family:Arial,sans-serif;width:150mm;padding:4mm 6mm;background:#fff">
        <!-- HEADER -->
        <div style="display:flex;align-items:flex-start;justify-content:space-between;margin-bottom:6px;border-bottom:1.5px solid #1e40af;padding-bottom:6px">
            <div style="display:flex;align-items:center;gap:8px">
                <div style="width:32px;height:32px;border-radius:50%;background:#1e3a8a;display:flex;align-items:center;justify-content:center">
                    <span style="color:#fff;font-size:8px;font-weight:900">MS</span>
                </div>
                <div>
                    <div style="color:#1e3a8a;font-size:6.5px;font-weight:700;letter-spacing:0.15em;text-transform:uppercase">Mega Soft Hygienic Products, Inc.</div>
                    <div style="color:#0f172a;font-size:14px;font-weight:900;letter-spacing:0.02em">Scrap Loading Form</div>
                </div>
            </div>
            <div style="text-align:right">
                <div style="font-size:6.5px;color:#64748b;font-weight:700;text-transform:uppercase;letter-spacing:0.1em">Control No.</div>
                <div style="font-size:18px;font-weight:900;color:#1e3a8a;letter-spacing:2px;font-family:monospace">${esc(controlNo)}</div>
            </div>
        </div>

        <!-- META ROW -->
        <div style="display:flex;gap:12px;margin-bottom:6px;font-size:8px">
            <div><span style="color:#64748b;font-weight:700">Date: </span><span style="font-weight:700;color:#0f172a">${esc(f.formDate(data))}</span></div>
            <div style="flex:1"><span style="color:#64748b;font-weight:700">Customer Name: </span><span style="font-weight:700;color:#0f172a">${esc(f.customerName(data))}</span></div>
        </div>
        <div style="display:flex;gap:16px;margin-bottom:8px;font-size:8px">
            <div style="display:flex;align-items:center;gap:4px">
                <div style="width:10px;height:10px;border:1px solid #1e40af;border-radius:2px;display:flex;align-items:center;justify-content:center;background:${caloocan ? '#1e40af' : '#fff'}">
                    ${caloocan ? '<span style="color:#fff;font-size:8px;line-height:1">✓</span>' : ''}
                </div>
                <span style="color:#334155;font-weight:600">Scrap From Caloocan Plant</span>
            </div>
            <div style="display:flex;align-items:center;gap:4px">
                <div style="width:10px;height:10px;border:1px solid #1e40af;border-radius:2px;display:flex;align-items:center;justify-content:center;background:${cdo ? '#1e40af' : '#fff'}">
                    ${cdo ? '<span style="color:#fff;font-size:8px;line-height:1">✓</span>' : ''}
                </div>
                <span style="color:#334155;font-weight:600">Scrap From CDO Plant</span>
            </div>
        </div>

        <!-- TABLE -->
        <table style="width:100%;border-collapse:collapse;margin-bottom:8px">
            <thead>
                <tr style="background:#1e3a8a">
                    <th style="padding:4px 6px;color:#fff;font-size:7.5px;text-align:center;width:18px">#</th>
                    <th style="padding:4px 8px;color:#fff;font-size:7.5px;text-align:left">Scrap Code-Scrap Name</th>
                    <th style="padding:4px 6px;color:#fff;font-size:7.5px;text-align:center;width:50px">Tag No.</th>
                    <th style="padding:4px 8px;color:#fff;font-size:7.5px;text-align:right;width:60px">Quantity</th>
                    <th style="padding:4px 6px;color:#fff;font-size:7.5px;text-align:center;width:36px">UOM</th>
                </tr>
            </thead>
            <tbody>${rowsHTML}</tbody>
        </table>

        <!-- REMARKS -->
        <div style="margin-bottom:12px">
            <span style="font-size:7.5px;font-weight:700;color:#64748b;text-transform:uppercase;letter-spacing:0.1em">Remarks: </span>
            <span style="font-size:8.5px;color:#0f172a">${esc(f.remarks(data))}</span>
        </div>

        <!-- SIGNATURES -->
        <div style="display:flex;gap:12px;margin-top:8px">
            ${["Prepared By", "Checked By", "Received By"].map((label, i) => {
        const val = [f.preparedBy(data), f.checkedBy(data), f.receivedBy(data)][i];
        return `<div style="flex:1;text-align:center">
                    <div style="border-top:1.5px solid #0f172a;padding-top:4px;min-width:80px;display:inline-block;min-height:28px">
                        <div style="font-size:9px;font-weight:700;color:#0f172a">${esc(val || "")}</div>
                    </div>
                    <div style="font-size:7.5px;color:#64748b;margin-top:3px;font-style:italic">${label}:</div>
                </div>`;
    }).join("")}
        </div>

        <!-- FOOTER -->
        <div style="margin-top:8px;padding-top:5px;border-top:0.5px solid #e2e8f0;text-align:center">
            <p style="font-size:6.5px;color:#94a3b8;font-style:italic;margin:0">
                This document is a property of Mega Soft Hygienic Products, Inc.
            </p>
        </div>
    </div>`;

    const el = document.createElement("div");
    el.innerHTML = html;
    document.body.appendChild(el);
    try {
        await window.html2pdf(el, {
            margin: [3, 3, 3, 3],
            filename: `ScrapLoading_${controlNo}.pdf`,
            image: { type: "jpeg", quality: 0.98 },
            html2canvas: { scale: 2, useCORS: true, logging: false },
            jsPDF: { unit: "mm", format: "a4", orientation: "portrait" },
        });
    } finally {
        document.body.removeChild(el);
    }
};

// ── Inline row ─────────────────────────────────────────────────────────────────

const ItemRow = ({ index, item, onChange, onDelete }) => {
    const [showSug, setShowSug] = useState(false);
    const [dropPos, setDropPos] = useState({ top: 0, left: 0 });

    const filtered = SCRAP_SUGGESTIONS.filter(s =>
        !item.scrapCodeName || s.toLowerCase().includes(item.scrapCodeName.toLowerCase())
    );

    return (
        <tr className={`border-b border-gray-100 ${index % 2 === 0 ? "bg-white" : "bg-slate-50/60"}`}>
            <td className="px-2 py-1 text-center text-slate-400 text-[10px] font-mono w-8">{index + 1}</td>

            <td className="px-1.5 py-1">
                <input
                    type="text"
                    value={item.scrapCodeName}
                    placeholder="e.g. GOOD KARTON"
                    autoComplete="off"
                    onChange={e => { onChange("scrapCodeName", e.target.value); setShowSug(true); }}
                    onFocus={e => {
                        const rect = e.target.getBoundingClientRect();
                        setDropPos({ top: rect.bottom + 2, left: rect.left });
                        setShowSug(true);
                    }}
                    onBlur={() => setTimeout(() => setShowSug(false), 150)}
                    className="w-full border border-gray-200 rounded px-2 py-1 text-xs focus:outline-none focus:ring-1 focus:ring-blue-400 bg-white font-semibold"
                />
                {showSug && filtered.length > 0 && createPortal(
                    <div style={{
                        position: "fixed",
                        top: dropPos.top,
                        left: dropPos.left,
                        minWidth: "180px",
                        zIndex: 9999,
                        background: "#fff",
                        border: "1px solid #e2e8f0",
                        borderRadius: "8px",
                        boxShadow: "0 4px 16px rgba(0,0,0,0.12)",
                        overflow: "hidden"
                    }}>
                        {filtered.map(s => (
                            <button
                                key={s}
                                onMouseDown={() => { onChange("scrapCodeName", s); setShowSug(false); }}
                                className="block w-full text-left px-3 py-1.5 text-xs hover:bg-blue-50 text-slate-700 font-medium cursor-pointer"
                                style={{ border: "none", background: "transparent" }}
                            >
                                {s}
                            </button>
                        ))}
                    </div>,
                    document.body
                )}
            </td>

            <td className="px-1.5 py-1 w-20">
                <input type="text" value={item.tagNo} placeholder="—"
                    onChange={e => onChange("tagNo", e.target.value)}
                    className="w-full border border-gray-200 rounded px-2 py-1 text-xs text-center focus:outline-none focus:ring-1 focus:ring-blue-400 bg-white font-mono" />
            </td>

            <td className="px-1.5 py-1 w-24">
                <input type="number" value={item.quantity === 0 ? "" : item.quantity}
                    placeholder="" min="0.001" step="0.001"
                    onChange={e => onChange("quantity", e.target.value)}
                    className="w-full border border-gray-200 rounded px-2 py-1 text-xs text-right focus:outline-none focus:ring-1 focus:ring-blue-400 bg-white font-semibold" />
            </td>

            <td className="px-1.5 py-1 w-20">
                <select value={item.uom} onChange={e => onChange("uom", e.target.value)}
                    className="w-full border border-gray-200 rounded px-1.5 py-1 text-xs focus:outline-none focus:ring-1 focus:ring-blue-400 bg-white">
                    <option value="">—</option>
                    {UOM_OPTIONS.map(u => <option key={u} value={u}>{u}</option>)}
                </select>
            </td>
            <td className="px-1 py-1 w-8 text-center">
                <button
                    onClick={() => onDelete(index)}
                    className="text-slate-300 hover:text-red-500 transition-colors cursor-pointer"
                    style={{ border: "none", background: "transparent", padding: "2px 4px" }}
                >
                    <X size={13} />
                </button>
            </td>
        </tr>
    );
};

// ── Document preview ──────────────────────────────────────────────────────────
const ScrapLoadingDocument = ({ data }) => {
    const controlNo = f.controlNo(data);
    const items = f.items(data);

    return (
        <div style={{ fontFamily: "Arial, sans-serif" }}>
            {/* Header */}
            <div style={{ background: "linear-gradient(135deg,#0c1445,#1a2f7a,#1e40af)", padding: "14px 20px", display: "flex", alignItems: "center", justifyContent: "space-between" }}>
                <div style={{ display: "flex", alignItems: "center", gap: "10px" }}>
                    <div style={{ width: "40px", height: "40px", borderRadius: "50%", border: "2px solid rgba(255,255,255,0.4)", display: "flex", alignItems: "center", justifyContent: "center", background: "rgba(255,255,255,0.1)" }}>
                        <span style={{ color: "#fff", fontSize: "9px", fontWeight: "900" }}>MS</span>
                    </div>
                    <div>
                        <div style={{ color: "#93c5fd", fontSize: "8px", fontWeight: "700", letterSpacing: "0.15em", textTransform: "uppercase" }}>Mega Soft Hygienic Products, Inc.</div>
                        <div style={{ color: "#fff", fontSize: "17px", fontWeight: "900" }}>Scrap Loading Form</div>
                    </div>
                </div>
                <div style={{ textAlign: "right", background: "rgba(255,255,255,0.1)", border: "0.5px solid rgba(255,255,255,0.2)", borderRadius: "8px", padding: "7px 16px" }}>
                    <div style={{ color: "#93c5fd", fontSize: "7.5px", fontWeight: "700", letterSpacing: "0.15em", textTransform: "uppercase" }}>Control No.</div>
                    <div style={{ color: "#fff", fontSize: "20px", fontWeight: "900", letterSpacing: "3px", fontFamily: "monospace" }}>{controlNo}</div>
                </div>
            </div>

            {/* Meta */}
            <div style={{ padding: "10px 20px", background: "#f8fafc", borderBottom: "0.5px solid #e2e8f0", display: "flex", flexWrap: "wrap", gap: "12px", alignItems: "center" }}>
                <div style={{ display: "flex", gap: "4px", fontSize: "11px" }}>
                    <span style={{ color: "#64748b", fontWeight: "700" }}>Date:</span>
                    <span style={{ color: "#0f172a", fontWeight: "700" }}>{f.formDate(data)}</span>
                </div>
                <span style={{ width: "1px", height: "14px", background: "#cbd5e1" }} />
                <div style={{ display: "flex", gap: "4px", fontSize: "11px", flex: 1 }}>
                    <span style={{ color: "#64748b", fontWeight: "700" }}>Customer Name:</span>
                    <span style={{ color: "#0f172a", fontWeight: "700" }}>{f.customerName(data)}</span>
                </div>
                <div style={{ display: "flex", gap: "16px", fontSize: "11px" }}>
                    {[["Scrap From Caloocan Plant", f.caloocan(data)], ["Scrap From CDO Plant", f.cdo(data)]].map(([label, checked]) => (
                        <div key={label} style={{ display: "flex", alignItems: "center", gap: "5px" }}>
                            <div style={{ width: "14px", height: "14px", border: `1.5px solid ${checked ? "#1e40af" : "#cbd5e1"}`, borderRadius: "3px", background: checked ? "#1e40af" : "#fff", display: "flex", alignItems: "center", justifyContent: "center" }}>
                                {checked && <span style={{ color: "#fff", fontSize: "9px", fontWeight: "900", lineHeight: 1 }}>✓</span>}
                            </div>
                            <span style={{ color: "#334155", fontWeight: "600" }}>{label}</span>
                        </div>
                    ))}
                </div>
            </div>

            {/* Table */}
            <div style={{ padding: "12px 20px" }}>
                <table style={{ width: "100%", borderCollapse: "collapse" }}>
                    <thead>
                        <tr style={{ background: "#1e3a8a" }}>
                            {["#", "Scrap Code-Scrap Name", "Tag No.", "Quantity", "UOM"].map((h, i) => (
                                <th key={h} style={{ padding: "6px 8px", color: "#fff", fontSize: "9px", fontWeight: "700", textTransform: "uppercase", letterSpacing: "0.05em", textAlign: ["#", "Tag No.", "Quantity", "UOM"].includes(h) ? "center" : "left" }}>{h}</th>
                            ))}
                        </tr>
                    </thead>
                    <tbody>
                        {Array.from({ length: TOTAL_ROWS }, (_, i) => {
                            const item = items[i] || {};
                            return (
                                <tr key={i} style={{ borderBottom: "0.5px solid #f1f5f9", background: i % 2 === 0 ? "#fff" : "#f8fafc" }}>
                                    <td style={{ padding: "4px 8px", textAlign: "center", fontSize: "9px", color: "#94a3b8", width: "24px" }}>{i + 1}</td>
                                    <td style={{ padding: "4px 8px", fontSize: "10px", fontWeight: item.scrapCodeName ? "700" : "400", color: item.scrapCodeName ? "#0f172a" : "#e2e8f0" }}>
                                        {item.scrapCodeName || "\u00A0"}
                                    </td>
                                    <td style={{ padding: "4px 8px", textAlign: "center", fontSize: "10px", color: "#334155" }}>{item.tagNo || ""}</td>
                                    <td style={{ padding: "4px 8px", textAlign: "right", fontSize: "10px", fontWeight: "600", color: "#0f172a" }}>{item.quantity ?? ""}</td>
                                    <td style={{ padding: "4px 8px", textAlign: "center", fontSize: "10px", color: "#64748b" }}>{item.uom || ""}</td>
                                </tr>
                            );
                        })}
                    </tbody>
                </table>
            </div>

            {/* Remarks */}
            <div style={{ padding: "0 20px 10px" }}>
                <span style={{ fontSize: "9px", fontWeight: "700", color: "#64748b", textTransform: "uppercase", letterSpacing: "0.1em" }}>Remarks: </span>
                <span style={{ fontSize: "11px", color: "#0f172a" }}>{f.remarks(data) || "\u00A0"}</span>
            </div>

            {/* Signatures */}
            <div style={{ display: "flex", gap: "0", padding: "10px 20px 16px", borderTop: "0.5px solid #e2e8f0" }}>
                {[["Prepared By", f.preparedBy(data)], ["Checked By", f.checkedBy(data)], ["Received By", f.receivedBy(data)]].map(([label, val]) => (
                    <div key={label} style={{ flex: 1, textAlign: "center" }}>
                        <div style={{ fontSize: "12px", fontWeight: "700", color: "#0f172a", borderBottom: "1.5px solid #0f172a", paddingBottom: "4px", minHeight: "28px", display: "inline-block", minWidth: "120px" }}>{val || "\u00A0"}</div>
                        <div style={{ fontSize: "8px", color: "#94a3b8", marginTop: "4px", fontStyle: "italic" }}>{label}:</div>
                    </div>
                ))}
            </div>

            {/* Footer */}
            <div style={{ padding: "8px 20px", borderTop: "0.5px solid #e2e8f0", background: "#f8fafc", textAlign: "center" }}>
                <p style={{ fontSize: "7.5px", color: "#94a3b8", fontStyle: "italic", margin: 0 }}>
                    This document is a property of Mega Soft Hygienic Products, Inc. Unauthorized copying, reproduction, distribution, or alteration is strictly prohibited.
                </p>
            </div>
        </div>
    );
};

// ── MAIN ─────────────────────────────────────────────────────────────────────
const ScrapLoadingForm = () => {
    const [currentUser, setCurrentUser] = useState(null);
    useEffect(() => {
        const stored = localStorage.getItem("user");
        if (stored) { try { setCurrentUser(JSON.parse(stored)); } catch { } }
    }, []);
    const userName = currentUser?.fullName || currentUser?.name || currentUser?.username || "Unknown User";
    const [selectedSORow, setSelectedSORow] = useState(null);
    const [showMultiModal, setShowMultiModal] = useState(false);
    const [multiScrapName, setMultiScrapName] = useState("");
    const [multiRowCount, setMultiRowCount] = useState(5);
    const [records, setRecords] = useState([]);
    const [loading, setLoading] = useState(false);
    const [search, setSearch] = useState("");
    const [currentPage, setCurrentPage] = useState(1);
    const [selectedRow, setSelectedRow] = useState(null);
    const [showForm, setShowForm] = useState(false);
    const [formLoading, setFormLoading] = useState(false);
    const [pdfLoading, setPdfLoading] = useState(false);
    const [hasDraft, setHasDraft] = useState(() => !!localStorage.getItem(DRAFT_KEY));
    const [form, setForm] = useState(EMPTY_FORM());

    // ── Fetch ─────────────────────────────────────────────────
    const fetchRecords = useCallback(async () => {
        setLoading(true);
        try {
            const res = await fetch(`${API_BASE_URL}api/ScrapLoadingForm`);
            if (!res.ok) throw new Error(`Server error: ${res.status}`);
            setRecords(await res.json());
        } catch (err) {
            Swal.fire({ icon: "error", title: "Failed to load records", text: err.message, confirmButtonColor: "#dc2626" });
        } finally { setLoading(false); }
    }, []);
    useEffect(() => { fetchRecords(); }, [fetchRecords]);

    // ── Control No generator ──────────────────────────────────
    const generateControlNo = useCallback(() => {
        const prefix = "0000";
        const max = records
            .map(r => parseInt(f.controlNo(r), 10))
            .filter(n => !isNaN(n))
            .reduce((m, n) => Math.max(m, n), 0);
        return String(max + 1).padStart(8, "0");
    }, [records]);

    // ── Draft ─────────────────────────────────────────────────
    const saveDraft = () => {
        localStorage.setItem(DRAFT_KEY, JSON.stringify(form));
        setHasDraft(true);
        Swal.fire({ icon: "success", title: "Draft Saved!", timer: 1500, showConfirmButton: false });
    };
    const clearDraft = () => { localStorage.removeItem(DRAFT_KEY); setHasDraft(false); };

    const openNew = () => {
        setForm({ ...EMPTY_FORM(), controlNo: generateControlNo(), preparedBy: userName });
        setShowForm(true);
    };
    const openDraft = () => {
        const raw = localStorage.getItem(DRAFT_KEY);
        if (!raw) return;
        try {
            const parsed = JSON.parse(raw);
            parsed.controlNo = generateControlNo();
            setForm(parsed);
        } catch { }
        setShowForm(true);
    };

    // ── Item helper ───────────────────────────────────────────
    const updateItem = (idx, field, value) => {
        setForm(prev => {
            const items = [...prev.items];
            items[idx] = { ...items[idx], [field]: value };
            return { ...prev, items };
        });
    };
    const [showMultiSug, setShowMultiSug] = useState(false);
    const [multiDropPos, setMultiDropPos] = useState({ top: 0, left: 0, width: 0 });
    const handleSubmit = async () => {
        if (!form.controlNo)
            return Swal.fire({ icon: "warning", title: "Control No. missing", confirmButtonColor: "#1d4ed8" });
        if (!form.customerName.trim())
            return Swal.fire({ icon: "warning", title: "Customer Name required", confirmButtonColor: "#1d4ed8" });

        // ✅ Filter: may scrapCodeName lang, quantity > 0 lang, at convert properly
        const validItems = form.items
            .filter(i => {
                const name = String(i.scrapCodeName ?? "").trim();
                const qty = parseFloat(i.quantity);
                return name.length > 0 && !isNaN(qty) && qty > 0;
            })
            .map((item, idx) => ({
                lineNo: idx + 1,
                scrapCodeName: String(item.scrapCodeName).trim(),
                tagNo: String(item.tagNo ?? "").trim() || null,
                quantity: parseFloat(item.quantity),
                uom: item.uom || "KGS",
            }));

        if (validItems.length === 0)
            return Swal.fire({
                icon: "warning",
                title: "Walang valid na item",
                text: "Lagyan ng Scrap Name at Quantity (higit sa 0) ang kahit isang row.",
                confirmButtonColor: "#1d4ed8"
            });

        setFormLoading(true);
        try {
            const payload = {
                controlNo: form.controlNo,
                formDate: form.formDate || null,
                customerName: form.customerName.trim(),
                scrapFromCaloocan: form.scrapFromCaloocan,
                scrapFromCDO: form.scrapFromCDO,
                remarks: form.remarks?.trim() || null,
                preparedBy: form.preparedBy?.trim() || null,
                checkedBy: form.checkedBy?.trim() || null,
                receivedBy: form.receivedBy?.trim() || null,
                createdBy: userName,
                items: validItems,
            };

            console.log("📦 Payload being sent:", JSON.stringify(payload, null, 2)); // ← para makita sa console

            const res = await fetch(`${API_BASE_URL}api/ScrapLoadingForm`, {
                method: "POST",
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify(payload),
            });

            if (!res.ok) {
                const e = await res.json().catch(() => ({}));
                // ✅ Show full error details para malaman kung ano exact na problem
                throw new Error(e?.message || JSON.stringify(e) || `Server error: ${res.status}`);
            }

            await Swal.fire({ icon: "success", title: "Saved!", text: "Scrap Loading Form created.", timer: 2000, showConfirmButton: false });
            fetch(`${API_BASE_URL}api/Logs`, {
                method: "POST",
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify({
                    action: "CREATE", module: "Scrap Loading Form",
                    description: `Created Scrap Loading Form #${form.controlNo}`,
                    userId: currentUser?.id, username: currentUser?.username, fullName: userName
                }),
            }).catch(() => { });
            clearDraft(); setShowForm(false); fetchRecords();

        } catch (err) {
            Swal.fire({ icon: "error", title: "Save Failed", text: err.message, confirmButtonColor: "#dc2626" });
        } finally { setFormLoading(false); }
    };
    // ── Delete ────────────────────────────────────────────────
    const handleDelete = async (row) => {
        const id = f.id(row);
        const ctrlNo = f.controlNo(row);
        const { isConfirmed } = await Swal.fire({
            icon: "warning", title: "Delete Record?",
            html: `Delete <strong>${ctrlNo}</strong>?<br/>This action cannot be undone.`,
            showCancelButton: true, confirmButtonText: "Yes, Delete", cancelButtonText: "Cancel",
            confirmButtonColor: "#dc2626", cancelButtonColor: "#6b7280",
        });
        if (!isConfirmed) return;
        try {
            const res = await fetch(`${API_BASE_URL}api/ScrapLoadingForm/${id}`, { method: "DELETE" });
            if (!res.ok) { const e = await res.json().catch(() => ({})); throw new Error(e?.message || `Server error: ${res.status}`); }
            await Swal.fire({ icon: "success", title: "Deleted!", timer: 1800, showConfirmButton: false });
            fetch(`${API_BASE_URL}api/Logs`, {
                method: "POST", headers: { "Content-Type": "application/json" },
                body: JSON.stringify({ action: "DELETE", module: "Scrap Loading Form", description: `Deleted Scrap Loading Form #${ctrlNo}`, userId: currentUser?.id, username: currentUser?.username, fullName: userName }),
            }).catch(() => { });
            setCurrentPage(1);
            fetchRecords();
        } catch (err) {
            Swal.fire({ icon: "error", title: "Delete Failed", text: err.message, confirmButtonColor: "#dc2626" });
        }
    };

    // ── PDF ───────────────────────────────────────────────────
    const handleExportPDF = async (row) => {
        setPdfLoading(true);
        try { await exportPDF(row); }
        catch (err) { Swal.fire({ icon: "error", title: "PDF Export Failed", text: err.message, confirmButtonColor: "#dc2626" }); }
        finally { setPdfLoading(false); }
    };

    // ── Filter + paginate ─────────────────────────────────────
    const filtered = useMemo(() =>
        records.filter(row => {
            const q = search.toLowerCase();
            return !q || f.controlNo(row).toLowerCase().includes(q)
                || f.customerName(row).toLowerCase().includes(q)
                || f.preparedBy(row).toLowerCase().includes(q);
        }),
        [records, search]);

    const totalPages = Math.max(1, Math.ceil(filtered.length / PAGE_SIZE));
    const paginated = filtered.slice((currentPage - 1) * PAGE_SIZE, currentPage * PAGE_SIZE);
    const paginBtn = (d) => `px-3 py-1.5 rounded-md text-xs font-semibold border transition-all ${d ? "border-gray-200 bg-gray-50 text-gray-300 cursor-not-allowed" : "border-gray-300 bg-white text-gray-600 hover:bg-gray-50 cursor-pointer"}`;

    // ── Render ────────────────────────────────────────────────
    return (
        <div className="min-h-screen bg-slate-100 p-8 font-sans">
            <div className="bg-white rounded-2xl shadow-lg overflow-hidden w-full">

                {/* PAGE HEADER */}
                <div style={gradHdr} className="px-8 py-6 flex items-center justify-between">
                    <div className="flex items-center gap-4">
                        <div className="bg-white/15 border border-white/20 p-2.5 rounded-2xl"><Truck className="text-white" size={22} /></div>
                        <div>
                            <p className="text-blue-300 text-[10px] font-bold uppercase tracking-[0.2em] mb-0.5">Mega Soft · Scrap Management</p>
                            <h1 className="text-white font-extrabold text-xl leading-tight">Scrap Loading Form</h1>
                        </div>
                    </div>
                    <div className="flex items-center gap-3">
                        <div className="flex items-center gap-2 bg-white/15 border border-white/20 rounded-lg px-3 py-1.5">
                            <Users size={14} className="text-white/70" />
                            <span className="text-white text-sm font-semibold">{userName}</span>
                        </div>
                        {hasDraft && (
                            <button onClick={openDraft}
                                className="flex items-center gap-2 px-4 py-2 rounded-xl text-white text-sm font-bold border-0 cursor-pointer transition-all hover:-translate-y-0.5"
                                style={{ background: "linear-gradient(135deg,#92400e,#b45309)", boxShadow: "0 2px 10px rgba(180,83,9,0.40)" }}>
                                <FileText size={15} /> Restore Draft
                            </button>
                        )}
                        <button onClick={openNew}
                            className="flex items-center gap-2 px-4 py-2 rounded-xl text-white text-sm font-bold border-0 cursor-pointer transition-all hover:-translate-y-0.5"
                            style={{ background: "linear-gradient(135deg,#15803d,#16a34a)", boxShadow: "0 2px 10px rgba(22,163,74,0.35)" }}>
                            <Plus size={16} /> New Record
                        </button>
                    </div>
                </div>

                <div className="p-8">
                    {/* TOOLBAR */}
                    <div className="flex flex-wrap gap-3 mb-4 items-center">
                        <div className="relative flex-1 min-w-[220px]">
                            <Search size={15} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
                            <input type="text" placeholder="Search control no, customer, prepared by..."
                                value={search} onChange={e => { setSearch(e.target.value); setCurrentPage(1); }}
                                className="w-full pl-9 pr-4 py-2.5 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 bg-white" />
                        </div>
                        <button onClick={fetchRecords} className="flex items-center gap-1.5 px-3 py-2.5 rounded-lg border border-gray-300 bg-white text-gray-600 text-sm font-semibold hover:bg-gray-50 transition-colors cursor-pointer">
                            <RefreshCw size={15} />
                        </button>
                    </div>

                    {!loading && <p className="text-xs text-slate-500 mb-3">{filtered.length} record{filtered.length !== 1 ? "s" : ""} found</p>}

                    {/* TABLE */}
                    {loading ? (
                        <div className="flex items-center justify-center py-16 text-slate-400 gap-3">
                            <RefreshCw size={20} className="animate-spin" />
                            <span className="text-sm font-medium">Loading records...</span>
                        </div>
                    ) : paginated.length === 0 ? (
                        <div className="text-center py-16 text-slate-400 text-sm">No records found.</div>
                    ) : (
                        <div className="overflow-x-auto rounded-xl border border-gray-200">
                            <table className="w-full text-sm border-collapse">
                                <thead>
                                    <tr className="bg-blue-800 text-white">
                                        {["#", "Control No.", "Date", "Customer Name", "Plant", "Prepared By", "Actions"].map((h, i, arr) => (
                                            <th key={i} className={`px-3 py-3 text-left text-xs font-bold uppercase tracking-wide whitespace-nowrap ${h === "Actions" ? "text-center" : ""} ${i === 0 ? "rounded-tl-xl" : ""} ${i === arr.length - 1 ? "rounded-tr-xl" : ""}`}>{h}</th>
                                        ))}
                                    </tr>
                                </thead>
                                <tbody>
                                    {paginated.map((row, idx) => (
                                        <tr key={f.id(row) || idx} className={`border-b border-gray-100 transition-colors hover:bg-blue-50 ${idx % 2 === 0 ? "bg-white" : "bg-slate-50"}`}>
                                            <td className="px-3 py-3 text-center text-slate-400 text-xs w-10">{(currentPage - 1) * PAGE_SIZE + idx + 1}</td>
                                            <td className="px-3 py-3 font-bold text-blue-700 whitespace-nowrap font-mono">{f.controlNo(row) || "—"}</td>
                                            <td className="px-3 py-3 text-slate-600 whitespace-nowrap">{f.formDate(row) ? new Date(f.formDate(row)).toLocaleDateString("en-US") : "—"}</td>
                                            <td className="px-3 py-3 text-slate-700 font-semibold">{f.customerName(row) || "—"}</td>
                                            <td className="px-3 py-3">
                                                <div className="flex gap-1 flex-wrap">
                                                    {f.caloocan(row) && <span className="px-2 py-0.5 rounded-full text-[10px] font-bold bg-blue-100 text-blue-700">Caloocan</span>}
                                                    {f.cdo(row) && <span className="px-2 py-0.5 rounded-full text-[10px] font-bold bg-green-100 text-green-700">CDO</span>}
                                                </div>
                                            </td>
                                            <td className="px-3 py-3 text-slate-700">{f.preparedBy(row) || "—"}</td>
                                            <td className="px-3 py-3 text-center">
                                                <div className="flex items-center justify-center gap-2">
                                                    <button onClick={() => setSelectedRow(row)}
                                                        className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg border border-gray-300 bg-white text-gray-600 text-xs font-semibold hover:bg-blue-50 hover:border-blue-300 hover:text-blue-700 transition-all cursor-pointer">
                                                        <Eye size={13} /> View
                                                    </button>

                                                    <button onClick={() => setSelectedSORow(row)} className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg border border-gray-300 bg-white text-gray-600 text-xs font-semibold hover:bg-blue-50 hover:border-blue-300 hover:text-blue-700 transition-all cursor-pointer">

                                                        <FileText size={13} /> View SO
                                                    </button>
                                                    <button onClick={() => handleDelete(row)}
                                                        className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg border border-gray-300 bg-white text-gray-600 text-xs font-semibold hover:bg-red-50 hover:border-red-300 hover:text-red-600 transition-all cursor-pointer">
                                                        <Trash2 size={13} /> Delete
                                                    </button>
                                                </div>
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                    )}

                    {/* PAGINATION */}
                    {!loading && filtered.length > PAGE_SIZE && (
                        <div className="flex justify-between items-center mt-5">
                            <span className="text-xs text-slate-500">Showing {(currentPage - 1) * PAGE_SIZE + 1}–{Math.min(currentPage * PAGE_SIZE, filtered.length)} of {filtered.length}</span>
                            <div className="flex gap-1.5 items-center">
                                <button onClick={() => setCurrentPage(1)} disabled={currentPage === 1} className={paginBtn(currentPage === 1)}>«</button>
                                <button onClick={() => setCurrentPage(p => Math.max(1, p - 1))} disabled={currentPage === 1} className={paginBtn(currentPage === 1)}>‹</button>
                                {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
                                    let page;
                                    if (totalPages <= 5) page = i + 1;
                                    else if (currentPage <= 3) page = i + 1;
                                    else if (currentPage >= totalPages - 2) page = totalPages - 4 + i;
                                    else page = currentPage - 2 + i;
                                    return (
                                        <button key={page} onClick={() => setCurrentPage(page)}
                                            className={`px-3 py-1.5 rounded-md text-xs font-semibold border transition-all cursor-pointer ${page === currentPage ? "bg-blue-600 text-white border-blue-600" : "bg-white text-gray-600 border-gray-300 hover:bg-gray-50"}`}>
                                            {page}
                                        </button>
                                    );
                                })}
                                <button onClick={() => setCurrentPage(p => Math.min(totalPages, p + 1))} disabled={currentPage === totalPages} className={paginBtn(currentPage === totalPages)}>›</button>
                                <button onClick={() => setCurrentPage(totalPages)} disabled={currentPage === totalPages} className={paginBtn(currentPage === totalPages)}>»</button>
                            </div>
                        </div>
                    )}
                </div>

                {/* PAGE FOOTER */}
                <div className="px-8 py-3 border-t border-gray-100 bg-slate-50 flex justify-between items-center">
                    <p className="text-[10px] text-slate-400 italic">This document is a property of Mega Soft. Unauthorized copying, reproduction, distribution, or alteration is strictly prohibited.</p>
                    <span className="text-xs text-slate-400">{new Date().toLocaleDateString("en-US", { month: "long", day: "numeric", year: "numeric" })}</span>
                </div>
            </div>

            {/* ══ NEW RECORD MODAL ══ */}
            {showForm && (
                <div className="fixed inset-0 bg-black/70 backdrop-blur-sm flex items-center justify-center z-50 p-4"
                    onClick={e => { if (e.target === e.currentTarget) setShowForm(false); }}>
                    <div className="bg-white rounded-3xl w-full max-w-4xl max-h-[95vh] flex flex-col overflow-hidden"
                        style={{ boxShadow: "0 32px 80px rgba(0,0,0,0.40)" }}>

                        {/* Modal header */}
                        <div style={gradHdr} className="px-7 py-5 flex items-center justify-between flex-shrink-0">
                            <div className="flex items-center gap-3">
                                <div className="bg-white/15 border border-white/20 p-2.5 rounded-2xl"><Truck size={20} className="text-white" /></div>
                                <div>
                                    <p className="text-blue-300 text-[10px] font-bold uppercase tracking-[0.2em]">New Entry</p>
                                    <h2 className="text-white font-extrabold text-base">Scrap Loading Form</h2>
                                </div>
                            </div>
                            <div className="flex items-center gap-3">
                                <div className="bg-white/10 border border-white/20 rounded-xl px-4 py-2 text-right">
                                    <p className="text-blue-300 text-[9px] font-bold uppercase tracking-widest">Control No.</p>
                                    <p className="text-white font-extrabold text-sm tracking-widest font-mono">{form.controlNo}</p>
                                </div>
                                <button onClick={() => setShowForm(false)} className="bg-white/15 hover:bg-white/25 text-white rounded-xl w-9 h-9 flex items-center justify-center cursor-pointer transition-all" style={{ border: "none" }}>
                                    <X size={16} />
                                </button>
                            </div>
                        </div>

                        <div className="flex-1 overflow-y-auto p-7 bg-slate-50 space-y-5">

                            {/* Form header fields */}
                            <div className="bg-white rounded-2xl border border-gray-100 p-5" style={{ boxShadow: "0 2px 12px rgba(0,0,0,0.06)" }}>
                                <h3 className="text-xs font-bold uppercase tracking-widest text-slate-400 mb-4">Form Information</h3>
                                <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                                    <div>
                                        <label className="block text-xs font-bold text-slate-600 mb-1.5">Control No.</label>
                                        <div className="w-full border-2 border-blue-100 bg-blue-50 rounded-xl px-3 py-2.5 text-sm font-extrabold text-blue-700 tracking-widest select-all font-mono">{form.controlNo}</div>
                                    </div>
                                    <div>
                                        <label className="block text-xs font-bold text-slate-600 mb-1.5">Date</label>
                                        <input type="date" value={form.formDate} onChange={e => setForm(p => ({ ...p, formDate: e.target.value }))}
                                            className="w-full border-2 border-gray-200 rounded-xl px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 bg-white" />
                                    </div>
                                    <div className="md:col-span-2">
                                        <label className="block text-xs font-bold text-slate-600 mb-1.5">Customer Name</label>
                                        <input type="text" value={form.customerName} placeholder="e.g. MARLON"
                                            onChange={e => setForm(p => ({ ...p, customerName: e.target.value }))}
                                            className="w-full border-2 border-gray-200 rounded-xl px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 bg-white font-semibold" />
                                    </div>

                                    {/* Plant checkboxes */}
                                    <div className="md:col-span-4 flex gap-6">
                                        {[
                                            { key: "scrapFromCaloocan", label: "Scrap From Caloocan Plant" },
                                            { key: "scrapFromCDO", label: "Scrap From CDO Plant" },
                                        ].map(({ key, label }) => (
                                            <label key={key} className="flex items-center gap-2.5 cursor-pointer select-none">
                                                <button type="button" onClick={() => setForm(p => ({ ...p, [key]: !p[key] }))}
                                                    className="w-5 h-5 rounded border-2 flex items-center justify-center transition-all flex-shrink-0 cursor-pointer"
                                                    style={{ borderColor: form[key] ? "#1e40af" : "#d1d5db", background: form[key] ? "#1e40af" : "#fff", border: "none" }}>
                                                    {form[key]
                                                        ? <CheckSquare size={18} className="text-blue-600" />
                                                        : <Square size={18} className="text-gray-400" />}
                                                </button>
                                                <span className="text-sm font-semibold text-slate-700">{label}</span>
                                            </label>
                                        ))}
                                    </div>
                                </div>
                            </div>

                            {/* Items table */}
                            <div className="bg-white rounded-2xl border border-gray-100 overflow-hidden" style={{ boxShadow: "0 2px 12px rgba(0,0,0,0.06)" }}>
                                <div className="px-5 py-3.5 border-b border-gray-100 flex items-center justify-between">
                                    <h3 className="text-xs font-bold uppercase tracking-widest text-slate-400">Scrap Items </h3>
                                    <div className="flex items-center gap-2">
                                        <span className="text-xs text-slate-400">
                                            {form.items.filter(i => i.scrapCodeName || i.quantity).length} item(s) filled
                                        </span>
                                        <button
                                            type="button"
                                            onClick={() => setForm(p => ({ ...p, items: [...p.items, EMPTY_ITEM()] }))}
                                            className="flex items-center gap-1 px-2.5 py-1 rounded-lg text-xs font-bold text-green-700 cursor-pointer transition-all hover:bg-green-100"
                                            style={{ background: "#dcfce7", border: "1.5px solid #86efac" }}>
                                            <Plus size={12} /> Add Row
                                        </button>
                                        <button onClick={() => setShowMultiModal(true)}>
                                            + Multiple Rows
                                        </button>
                                    </div>                                </div>
                                <div className="overflow-x-auto">
                                    <table className="w-full text-sm border-collapse">
                                        <thead>
                                            <tr className="bg-blue-800 text-white">
                                                <th className="px-2 py-2 text-[10px] font-bold uppercase text-center w-8">#</th>
                                                <th className="px-2 py-2 text-[10px] font-bold uppercase text-left">Scrap Code-Scrap Name</th>
                                                <th className="px-2 py-2 text-[10px] font-bold uppercase text-center w-20">Tag No.</th>
                                                <th className="px-2 py-2 text-[10px] font-bold uppercase text-right w-24">Quantity</th>
                                                <th className="px-2 py-2 text-[10px] font-bold uppercase text-center w-20">UOM</th>
                                                <th className="px-2 py-2 text-[10px] font-bold uppercase text-center w-20"></th>

                                            </tr>
                                        </thead>
                                        <tbody>
                                            {form.items.map((item, idx) => (
                                                <ItemRow key={idx} index={idx} item={item}
                                                    onChange={(field, value) => updateItem(idx, field, value)}
                                                    onDelete={(i) => setForm(p => ({
                                                        ...p,
                                                        items: p.items.filter((_, fi) => fi !== i)
                                                    }))}
                                                />
                                            ))}
                         </tbody>
                        <tfoot>
                            <tr style={{ borderTop: "2px solid #e2e8f0", background: "#f8fafc" }}>
                                <td colSpan={3} className="px-3 py-2 text-right text-xs font-bold text-slate-500 uppercase tracking-wide">
                                    Total Quantity:
                                </td>
                                <td className="px-2 py-2 text-right">
                                    <span className="text-sm font-extrabold text-blue-700">
                                        {form.items
                                            .reduce((sum, i) => sum + (parseFloat(i.quantity) || 0), 0)
                                            .toLocaleString()}
                                    </span>
                                </td>
                                <td colSpan={2} />
                            </tr>
                            <tr style={{ background: "#eff6ff", borderTop: "1px solid #bfdbfe" }}>
                                <td colSpan={3} className="px-3 py-2 text-right text-xs font-bold text-blue-600 uppercase tracking-wide">
                                    Total × Rate:
                                </td>
                                <td className="px-2 py-2" colSpan={3}>
                                    <div className="flex items-center justify-end gap-2">
                                        {/* Total display */}
                                        <span className="text-xs font-bold text-slate-600">
                                            {form.items.reduce((sum, i) => sum + (parseFloat(i.quantity) || 0), 0).toLocaleString()}
                                        </span>
                                        <span className="text-xs text-slate-400">×</span>
                                        {/* Manual rate input */}
                                        <input
                                            type="number"
                                            value={form.rateMultiplier ?? ""}
                                            min="0"
                                            step="0.01"
                                            placeholder="rate"
                                            onChange={e => setForm(p => ({ ...p, rateMultiplier: e.target.value }))}
                                            style={{
                                                width: 70, border: "2px solid #bfdbfe", borderRadius: 6,
                                                padding: "3px 6px", fontSize: 12, fontWeight: 700,
                                                textAlign: "right", background: "#fff"
                                            }}
                                        />
                                        <span className="text-xs text-slate-400">=</span>
                                        {/* Result */}
                                        <span className="text-sm font-extrabold text-green-600">
                                            {(
                                                form.items.reduce((sum, i) => sum + (parseFloat(i.quantity) || 0), 0) *
                                                (parseFloat(form.rateMultiplier) || 0)
                                            ).toLocaleString()}
                                        </span>
                                    </div>
                                </td>
                            </tr>
                        </tfoot>
                    </table>
                </div>
            </div>

                            {/* Closing */}
                            <div className="bg-white rounded-2xl border border-gray-100 p-5" style={{ boxShadow: "0 2px 12px rgba(0,0,0,0.06)" }}>
                                <h3 className="text-xs font-bold uppercase tracking-widest text-slate-400 mb-4">Closing Information</h3>
                                <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
                                    <div className="md:col-span-2">
                                        <label className="block text-xs font-bold text-slate-600 mb-1.5">Remarks</label>
                                        <textarea value={form.remarks} rows={2} placeholder="Optional remarks..."
                                            onChange={e => setForm(p => ({ ...p, remarks: e.target.value }))}
                                            className="w-full border-2 border-gray-200 rounded-xl px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 bg-white resize-none" />
                                    </div>
                                </div>
                                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                                    {[
                                        { key: "preparedBy", label: "Prepared By" },
                                        { key: "checkedBy", label: "Checked By" },
                                        { key: "receivedBy", label: "Received By" },
                                    ].map(({ key, label }) => (
                                        <div key={key}>
                                            <label className="block text-xs font-bold text-slate-600 mb-1.5">{label}</label>
                                            <input type="text" value={form[key]} placeholder={`${label} name...`}
                                                onChange={e => setForm(p => ({ ...p, [key]: e.target.value }))}
                                                className="w-full border-2 border-gray-200 rounded-xl px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 bg-white" />
                                        </div>
                                    ))}
                                </div>
                            </div>
                        </div>

                        {/* Modal footer */}
                        <div className="px-7 py-4 bg-white border-t border-gray-100 flex items-center justify-between flex-shrink-0"
                            style={{ boxShadow: "0 -4px 16px rgba(0,0,0,0.06)" }}>
                            <button onClick={saveDraft} className="flex items-center gap-2 px-4 py-2.5 rounded-xl text-amber-700 text-sm font-bold transition-all cursor-pointer hover:-translate-y-0.5"
                                style={{ background: "#fef3c7", border: "2px solid #fde68a" }}>
                                <FileText size={14} /> Save Draft
                            </button>
                            <div className="flex items-center gap-3">
                                <button onClick={() => setShowForm(false)} className="flex items-center gap-2 px-5 py-2.5 rounded-xl border-2 border-gray-200 bg-gray-50 hover:bg-gray-100 text-gray-600 text-sm font-bold transition-all cursor-pointer">
                                    <X size={14} /> Cancel
                                </button>
                                <button onClick={handleSubmit} disabled={formLoading}
                                    className="flex items-center gap-2 px-6 py-2.5 rounded-xl text-white text-sm font-bold cursor-pointer disabled:opacity-50 transition-all hover:-translate-y-0.5"
                                    style={{ background: "linear-gradient(135deg,#1a2f7a,#1e40af)", boxShadow: "0 2px 10px rgba(30,64,175,0.35)", border: "none" }}>
                                    <Save size={15} />
                                    {formLoading ? "Saving..." : "Save Record"}
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
            )}

            {/* ══ VIEW MODAL ══ */}
            {selectedRow && (
                <div className="fixed inset-0 bg-black/70 backdrop-blur-sm flex items-center justify-center z-50 p-4"
                    onClick={e => { if (e.target === e.currentTarget) setSelectedRow(null); }}>
                    <div className="bg-white rounded-3xl w-full max-w-4xl max-h-[95vh] flex flex-col overflow-hidden"
                        style={{ boxShadow: "0 32px 80px rgba(0,0,0,0.40)" }}>

                        <div style={gradHdr} className="px-7 py-5 flex items-center justify-between flex-shrink-0">
                            <div className="flex items-center gap-4">
                                <div className="bg-white/15 border border-white/20 p-2.5 rounded-2xl"><Truck size={20} className="text-white" /></div>
                                <div>
                                    <p className="text-blue-300 text-[10px] font-bold uppercase tracking-[0.2em] mb-0.5">Mega Soft · Scrap Management</p>
                                    <h2 className="text-white font-extrabold text-base leading-none">Scrap Loading Form</h2>
                                </div>
                            </div>
                            <div className="flex items-center gap-3">
                                <div className="text-right bg-white/10 border border-white/20 rounded-xl px-4 py-2">
                                    <p className="text-blue-300 text-[9px] font-bold uppercase tracking-widest">Control No.</p>
                                    <p className="text-white font-extrabold text-sm tracking-wider font-mono">{f.controlNo(selectedRow)}</p>
                                </div>
                                <button onClick={() => setSelectedRow(null)} className="bg-white/15 hover:bg-white/25 text-white rounded-xl w-9 h-9 flex items-center justify-center transition-all cursor-pointer" style={{ border: "none" }}>
                                    <X size={16} />
                                </button>
                            </div>
                        </div>

                        {/* Meta bar */}
                        <div className="px-7 py-2.5 flex items-center gap-1 flex-wrap flex-shrink-0" style={{ background: "rgba(12,20,69,0.93)" }}>
                            {[
                                { icon: CalendarDays, label: "Date", value: f.formDate(selectedRow) ? new Date(f.formDate(selectedRow)).toLocaleDateString("en-US", { month: "long", day: "numeric", year: "numeric" }) : "—" },
                                { icon: Users, label: "Customer", value: f.customerName(selectedRow) || "—" },
                                { icon: Building2, label: "Prepared By", value: f.preparedBy(selectedRow) || "—" },
                            ].map((item, i) => {
                                const Icon = item.icon;
                                return (
                                    <React.Fragment key={i}>
                                        {i > 0 && <span className="w-px h-4 bg-white/20 mx-3" />}
                                        <span className="flex items-center gap-2 text-xs">
                                            <Icon size={12} className="text-blue-400" />
                                            <span className="text-white/50">{item.label}:</span>
                                            <span className="text-white font-semibold">{item.value}</span>
                                        </span>
                                    </React.Fragment>
                                );
                            })}
                        </div>

                        <div className="flex-1 overflow-y-auto" style={{ background: "#e8edf5" }}>
                            <div className="p-6">
                                <div className="bg-white rounded-2xl overflow-hidden" style={{ boxShadow: "0 8px 32px rgba(0,0,0,0.12)" }}>
                                    <ScrapLoadingDocument data={selectedRow} />
                                </div>
                            </div>
                        </div>

                        <div className="px-7 py-4 bg-white border-t border-gray-100 flex items-center justify-between flex-shrink-0"
                            style={{ boxShadow: "0 -4px 16px rgba(0,0,0,0.06)" }}>
                            <button onClick={() => handleExportPDF(selectedRow)} disabled={pdfLoading}
                                className="flex items-center gap-2 px-5 py-2.5 rounded-xl text-white text-sm font-bold disabled:opacity-50 transition-all cursor-pointer hover:-translate-y-0.5"
                                style={{ background: "linear-gradient(135deg,#dc2626,#b91c1c)", boxShadow: "0 2px 10px rgba(220,38,38,0.35)", border: "none" }}>
                                <Download size={15} />
                                {pdfLoading ? "Generating PDF..." : "Export PDF"}
                            </button>
                            <button onClick={() => setSelectedRow(null)}
                                className="flex items-center gap-2 px-5 py-2.5 rounded-xl border-2 border-gray-200 bg-gray-50 hover:bg-gray-100 text-gray-600 text-sm font-bold transition-all cursor-pointer">
                                <X size={14} /> Close
                            </button>
                        </div>
                    </div>
                </div>
            )}
            {selectedSORow && (
                <ViewSOModal row={selectedSORow} onClose={() => setSelectedSORow(null)} />
            )}
            {showMultiModal && (
                <div style={{
                    position: "fixed", inset: 0, background: "rgba(0,0,0,0.5)",
                    display: "flex", alignItems: "center", justifyContent: "center", zIndex: 60
                }}
                    onClick={e => { if (e.target === e.currentTarget) setShowMultiModal(false) }}>
                    <div style={{
                        background: "#fff", borderRadius: 16, padding: 24, width: 340,
                        boxShadow: "0 8px 32px rgba(0,0,0,0.2)"
                    }}>
                        <h3 style={{ margin: "0 0 16px", fontSize: 14, fontWeight: 700, color: "#1e40af" }}>
                            Add Multiple Rows
                        </h3>

                        <label style={{ fontSize: 12, fontWeight: 600, color: "#64748b", display: "block", marginBottom: 4 }}>
                            Scrap Code-Scrap Name
                        </label>

                        {/* ← WRAPPER para sa positioning ng dropdown */}
                        <div style={{ position: "relative", marginBottom: 14 }}>
                            <input
                                type="text"
                                value={multiScrapName}
                                onChange={e => { setMultiScrapName(e.target.value); setShowMultiSug(true); }}
                                onFocus={e => {
                                    const rect = e.target.getBoundingClientRect();
                                    setMultiDropPos({ top: rect.bottom + 2, left: rect.left, width: rect.width });
                                    setShowMultiSug(true);
                                }}
                                onBlur={() => setTimeout(() => setShowMultiSug(false), 150)}
                                placeholder="e.g. GOOD KARTON (optional)"
                                style={{
                                    width: "100%", border: "2px solid #e2e8f0", borderRadius: 8,
                                    padding: "8px 10px", fontSize: 13, boxSizing: "border-box"
                                }}
                            />

                            {showMultiSug && (() => {
                                const filteredMulti = SCRAP_SUGGESTIONS.filter(s =>
                                    !multiScrapName || s.toLowerCase().includes(multiScrapName.toLowerCase())
                                );
                                return filteredMulti.length > 0 && createPortal(
                                    <div style={{
                                        position: "fixed",
                                        top: multiDropPos.top,
                                        left: multiDropPos.left,
                                        width: multiDropPos.width,
                                        zIndex: 9999,
                                        background: "#fff",
                                        border: "1px solid #e2e8f0",
                                        borderRadius: 8,
                                        boxShadow: "0 4px 16px rgba(0,0,0,0.12)",
                                        overflow: "hidden"
                                    }}>
                                        {filteredMulti.map(s => (
                                            <button
                                                key={s}
                                                onMouseDown={() => { setMultiScrapName(s); setShowMultiSug(false); }}
                                                style={{
                                                    display: "block", width: "100%", textAlign: "left",
                                                    padding: "8px 12px", fontSize: 12, fontWeight: 500,
                                                    color: "#334155", background: "transparent",
                                                    border: "none", cursor: "pointer"
                                                }}
                                                onMouseEnter={e => e.target.style.background = "#eff6ff"}
                                                onMouseLeave={e => e.target.style.background = "transparent"}
                                            >
                                                {s}
                                            </button>
                                        ))}
                                    </div>,
                                    document.body
                                );
                            })()}
                        </div>

                        <label style={{ fontSize: 12, fontWeight: 600, color: "#64748b", display: "block", marginBottom: 4 }}>
                            Number of Rows
                        </label>
                        <input type="number" value={multiRowCount} min={1} max={50}
                            onChange={e => setMultiRowCount(Number(e.target.value))}
                            style={{
                                width: "100%", border: "2px solid #e2e8f0", borderRadius: 8,
                                padding: "8px 10px", fontSize: 13, marginBottom: 20, boxSizing: "border-box"
                            }} />

                        <div style={{ display: "flex", gap: 8, justifyContent: "flex-end" }}>
                            <button onClick={() => setShowMultiModal(false)}
                                style={{
                                    padding: "8px 16px", borderRadius: 8, border: "2px solid #e2e8f0",
                                    background: "#f8fafc", fontWeight: 600, fontSize: 13, cursor: "pointer"
                                }}>
                                Cancel
                            </button>
                            <button onClick={() => {
                                const newRows = Array.from({ length: multiRowCount }, () =>
                                    ({ ...EMPTY_ITEM(), scrapCodeName: multiScrapName })
                                );
                                setForm(p => ({ ...p, items: [...p.items, ...newRows] }));
                                setMultiScrapName(""); setMultiRowCount(5);
                                setShowMultiModal(false);
                            }} style={{
                                padding: "8px 20px", borderRadius: 8, border: "none",
                                background: "#1e40af", color: "#fff", fontWeight: 700, fontSize: 13, cursor: "pointer"
                            }}>
                                Add Rows
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default ScrapLoadingForm;